import { useState, useEffect } from "react";
import { CheckCircle, AlertTriangle, Lock, Info } from "lucide-react";
import store from "../store";

/**
 * usePlan Hook
 *
 * Provides centralized plan management and parsing logic.
 * Reads plan string directly from store.restaurantInfo.description.
 *
 * Plan String Format: PLAN_TYPE|STATUS|START_DATE|END_DATE
 * - PLAN_TYPE: P (Paid), U (Unpaid), F (Free)
 * - STATUS: ACTIVE, EXPIRED, DUE
 * - START_DATE: YYYY-MM-DD format
 * - END_DATE: YYYY-MM-DD format
 * - AMOUNT: Amount of the plan
 *
 * Examples:
 * - P|ACTIVE|2025-10-01|2025-11-30|2999 (Paid user, valid till 30th Nov 2025, amount ₹2999)
 * - U|DUE|2025-10-01|2025-10-15|1999 (Unpaid user, due on 15th Oct 2025, amount ₹1999)
 * - F|ACTIVE|2025-09-01|2025-10-10|0 (Free user, blocked after 10th Oct 2025)
 * - P|EXPIRED|2025-07-01|2025-08-01|2999 (Expired Paid plan, amount ₹2999)
 *
 * Data Source: store.restaurantInfo.description
 *
 * Returns:
 * - plan: Current plan object with all parsed information
 * - planString: Raw plan string from description
 * - isPaid: Boolean indicating if user has paid plan
 * - isExpired: Boolean indicating if plan is expired
 * - isDue: Boolean indicating if payment is due
 * - isFree: Boolean indicating if user is on free trial
 */

// Plan parsing utilities
const parsePlan = (planString) => {
  if (!planString) return null;

  const [type, status, start, end, amount] = planString.split("|");

  const planTypes = {
    P: "Paid",
    U: "Unpaid",
    F: "Free",
  };

  const statusColors = {
    ACTIVE: "text-black-600",
    DUE: "text-yellow-600",
    EXPIRED: "text-red-600",
  };

  const statusIcons = {
    ACTIVE: CheckCircle,
    DUE: AlertTriangle,
    EXPIRED: Lock,
  };

  return {
    planType: planTypes[type] || "Unknown",
    status,
    startDate: start ? formatDate(start) : null,
    endDate: end ? formatDate(end) : null,
    amount: amount ? parseFloat(amount) : null,
    statusColor: statusColors[status] || "text-gray-600",
    statusIcon: statusIcons[status] || Info,
    isUrgent: status === "DUE" || status === "EXPIRED",
  };
};

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  // Convert YYYY-MM-DD to dd MMM YYYY format
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short', 
    year: 'numeric'
  });
};

const formatAmount = (amount) => {
  if (!amount || amount === 0) return null;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const getDaysUntilExpiry = (endDate) => {
  if (!endDate) return null;

  const today = new Date();
  // Handle YYYY-MM-DD format
  const expiryDate = new Date(endDate);

  const diffTime = expiryDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

const getStatusMessage = (planData) => {
  if (!planData) return "No plan information available";

  const { planType, status, endDate, amount } = planData;
  const daysUntilExpiry = getDaysUntilExpiry(endDate);

  // Check if plan is actually expired based on date (even if status is ACTIVE)
  const isActuallyExpired = daysUntilExpiry !== null && daysUntilExpiry <= 0;

  switch (status) {
    case "ACTIVE":
      // If ACTIVE but past expiry date, treat as expired
      if (isActuallyExpired) {
        return "Your subscription has expired. Renew now to restore access.";
      }
      
      if (planType === "Paid") {
        const amountText = formatAmount(amount) ? ` (${formatAmount(amount)})` : '';
        if (daysUntilExpiry !== null && daysUntilExpiry <= 5) {
          return `Your plan expires in ${daysUntilExpiry} days on ${formatDate(endDate)}${amountText}`;
        }
        return `Payment successful. Your plan is active until ${formatDate(endDate)}${amountText}`;
      } else if (planType === "Free") {
        return `Free trial ends ${formatDate(endDate)}. Upgrade to continue.`;
      } else if (planType === "Unpaid") {
        const amountText = formatAmount(amount) ? ` (${formatAmount(amount)})` : '';
        return `Extended plan will expire on ${formatDate(endDate)}${amountText}`;
      }
      return `${planType} plan active`;

    case "DUE":
      const amountText = formatAmount(amount) ? ` (${formatAmount(amount)})` : '';
      if (daysUntilExpiry !== null) {
        if (daysUntilExpiry > 0) {
          return `Payment due in ${daysUntilExpiry} days${amountText}. Update your payment method to avoid interruption.`;
        } else {
          return `Payment overdue${amountText} - please pay immediately`;
        }
      }
      return `Payment due in 20 days${amountText}. Update your payment method to avoid interruption.`;

    case "EXPIRED":
      return "Your subscription has expired. Renew now to restore access.";

    default:
      return "Plan status unknown";
  }
};

const usePlan = () => {
  const [plan, setPlan] = useState(null);
  const [planString, setPlanString] = useState(null);

  useEffect(() => {
    // Get plan string directly from store.restaurantInfo.description
    const currentPlanString = store?.restaurantInfo?.description;

    if (currentPlanString && currentPlanString.includes("|")) {
      // Valid plan string format
      setPlanString(currentPlanString);
      const parsedPlan = parsePlan(currentPlanString);
      setPlan(parsedPlan);
    } else {
      // No valid plan string, reset state
      setPlanString(null);
      setPlan(null);
    }
  }, [store?.restaurantInfo?.description]);

  // Computed properties
  const isPaid = plan?.planType === "Paid" && plan?.status === "ACTIVE";
  
  // isExpired: Check both status and actual expiry date
  const isExpired = plan?.status === "EXPIRED" || 
    (plan?.endDate && getDaysUntilExpiry(plan.endDate) <= 0);
  
  const isDue = plan?.status === "DUE";
  const isFree = plan?.planType === "Free";
  const isUnpaid = plan?.planType === "Unpaid";

  // Calculate days until expiry
  const daysUntilExpiry = plan?.endDate ? getDaysUntilExpiry(plan.endDate) : null;
  const isAboutToExpire = daysUntilExpiry !== null && daysUntilExpiry <= 5;

  // Should show payment reminder stripe
  const shouldShowStripe = !isPaid || (isPaid && isAboutToExpire);

  // Determine stripe color based on status
  const getStripeColor = () => {
    if (isExpired) return 'danger'; // Red for expired
    if (isAboutToExpire) return 'warning'; // Orange for about to expire
    if (isFree) return 'info'; // Blue for free plans
    if (isDue || isUnpaid) return 'warning'; // Orange for due/unpaid
    return 'info'; // Default blue
  };

  const stripeColor = getStripeColor();

  // Get status message
  const statusMessage = plan ? getStatusMessage(plan) : null;

  return {
    plan,
    planString,
    isPaid,
    isExpired,
    isDue,
    isFree,
    isUnpaid,
    statusMessage,
    daysUntilExpiry,
    isAboutToExpire,
    shouldShowStripe,
    stripeColor,
    // Utility functions
    formatDate,
    formatAmount,
    getDaysUntilExpiry,
    parsePlan,
  };
};

export default usePlan;
